import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useBackend } from '../hooks/useBackend';

const InfrastructurePage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { apiCall } = useBackend();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('overview');
  const [showDeployModal, setShowDeployModal] = useState(false);
  const [deployForm, setDeployForm] = useState({
    serviceName: '',
    serviceType: 'web-app',
    environment: 'production',
    replicas: 1,
    resources: {
      cpu: '500m',
      memory: '512Mi'
    },
    ports: [{ port: 80, targetPort: 8080 }]
  });
  const [infrastructureData, setInfrastructureData] = useState({
    servers: [],
    databases: [],
    services: [],
    metrics: {}
  });

  // Mock infrastructure data - in real app this would come from backend
  useEffect(() => {
    const loadInfrastructureData = async () => {
      try {
        setLoading(true);
        // Simulate API call
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        setInfrastructureData({
          servers: [
            { id: 1, name: 'orbit-web-01', status: 'running', cpu: 45, memory: 62, disk: 78, location: 'US-East-1' },
            { id: 2, name: 'orbit-api-01', status: 'running', cpu: 32, memory: 48, disk: 55, location: 'US-East-1' },
            { id: 3, name: 'orbit-worker-01', status: 'running', cpu: 78, memory: 85, disk: 42, location: 'US-West-2' },
            { id: 4, name: 'orbit-db-01', status: 'maintenance', cpu: 15, memory: 25, disk: 90, location: 'US-East-1' }
          ],
          databases: [
            { id: 1, name: 'orbit-primary', type: 'PostgreSQL', status: 'healthy', connections: 45, size: '2.3GB' },
            { id: 2, name: 'orbit-cache', type: 'Redis', status: 'healthy', connections: 128, size: '512MB' },
            { id: 3, name: 'orbit-analytics', type: 'MongoDB', status: 'warning', connections: 23, size: '8.7GB' }
          ],
          services: [
            { id: 1, name: 'API Gateway', status: 'running', version: 'v2.1.0', uptime: '99.9%', requests: '1.2M/day' },
            { id: 2, name: 'AI Processing', status: 'running', version: 'v1.8.3', uptime: '99.7%', requests: '450K/day' },
            { id: 3, name: 'Workflow Engine', status: 'running', version: 'v3.0.1', uptime: '99.8%', requests: '89K/day' },
            { id: 4, name: 'Notification Service', status: 'degraded', version: 'v1.2.1', uptime: '97.2%', requests: '25K/day' }
          ],
          metrics: {
            totalRequests: '1.76M',
            avgResponseTime: '245ms',
            errorRate: '0.12%',
            uptime: '99.8%'
          }
        });
      } catch (err) {
        setError('Failed to load infrastructure data');
      } finally {
        setLoading(false);
      }
    };

    loadInfrastructureData();
  }, []);

  const handleDeployService = async () => {
    try {
      setError('');
      setLoading(true);
      
      // Simulate API call to deploy service
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Add the new service to the list
      const newService = {
        id: infrastructureData.services.length + 1,
        name: deployForm.serviceName,
        status: 'running',
        version: 'v1.0.0',
        uptime: '100%',
        requests: '0/day'
      };
      
      setInfrastructureData(prev => ({
        ...prev,
        services: [...prev.services, newService]
      }));
      
      setShowDeployModal(false);
      setDeployForm({
        serviceName: '',
        serviceType: 'web-app',
        environment: 'production',
        replicas: 1,
        resources: {
          cpu: '500m',
          memory: '512Mi'
        },
        ports: [{ port: 80, targetPort: 8080 }]
      });
      
      // Show success message
      alert('Service deployed successfully!');
      
    } catch (err) {
      setError('Failed to deploy service');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    try {
      setError('');
      setLoading(true);
      
      // Simulate refresh API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // In a real app, this would reload data from the backend
      console.log('Infrastructure data refreshed');
      
    } catch (err) {
      setError('Failed to refresh data');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'running':
      case 'healthy':
        return 'text-green-600 bg-green-100 dark:bg-green-900/20 dark:text-green-400';
      case 'warning':
      case 'degraded':
        return 'text-yellow-600 bg-yellow-100 dark:bg-yellow-900/20 dark:text-yellow-400';
      case 'maintenance':
      case 'error':
        return 'text-red-600 bg-red-100 dark:bg-red-900/20 dark:text-red-400';
      default:
        return 'text-slate-600 bg-slate-100 dark:bg-slate-800 dark:text-slate-400';
    }
  };

  const getUsageColor = (percentage) => {
    if (percentage >= 80) return 'bg-red-500';
    if (percentage >= 60) return 'bg-yellow-500';
    return 'bg-green-500';
  };

  const tabs = [
    { id: 'overview', label: 'Overview', icon: 'dashboard' },
    { id: 'servers', label: 'Servers', icon: 'dns' },
    { id: 'databases', label: 'Databases', icon: 'storage' },
    { id: 'services', label: 'Services', icon: 'cloud' },
    { id: 'monitoring', label: 'Monitoring', icon: 'monitoring' },
    { id: 'logs', label: 'Logs', icon: 'description' }
  ];

  return (
    <div style={{ backgroundColor: '#0a1116', color: 'white', minHeight: '100vh', fontFamily: 'Manrope, sans-serif' }}>
      {/* Header */}
      <header className="sticky top-0 z-50 w-full border-b border-[#233c48] backdrop-blur-md px-4 md:px-10 py-3" style={{ backgroundColor: '#0a1116' }}>
        <div className="max-w-[1440px] mx-auto flex items-center justify-between whitespace-nowrap">
          <div className="flex items-center gap-8">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 text-[#13a4ec]">
                <svg fill="none" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="24" cy="24" r="20" stroke="currentColor" strokeDasharray="10 4" strokeWidth="4"></circle>
                  <circle cx="24" cy="24" fill="currentColor" r="8"></circle>
                  <path d="M40 24C40 32.8366 32.8366 40 24 40" stroke="currentColor" strokeLinecap="round" strokeWidth="2"></path>
                </svg>
              </div>
              <h2 className="text-xl font-extrabold leading-tight tracking-tighter hidden sm:block">OrbitMind</h2>
            </div>
            <div className="hidden lg:flex items-center gap-6">
              <button onClick={() => navigate('/services')} className="text-sm font-medium text-slate-400 hover:text-[#13a4ec] transition-colors">Services Hub</button>
              <button onClick={() => navigate('/workflows')} className="text-sm font-medium text-slate-400 hover:text-[#13a4ec] transition-colors">Workflows</button>
              <span className="text-sm font-semibold text-[#13a4ec] border-b-2 border-[#13a4ec] pb-1">Infrastructure</span>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <label className="hidden md:flex flex-col min-w-40 h-10 max-w-64">
              <div className="flex w-full flex-1 items-stretch rounded-lg h-full border border-[#233c48] bg-[#1a2b34]">
                <div className="flex items-center justify-center pl-4">
                  <span className="material-symbols-outlined text-slate-400 text-[20px]">search</span>
                </div>
                <input 
                  className="w-full border-none bg-transparent focus:ring-0 text-sm placeholder:text-slate-500 pl-2 text-white" 
                  placeholder="Search infrastructure..." 
                  type="text"
                />
              </div>
            </label>
            <div className="flex gap-2">
              <button className="flex items-center justify-center rounded-lg h-10 w-10 bg-[#233c48] hover:bg-[#13a4ec]/20 transition-colors">
                <span className="material-symbols-outlined text-[20px]">notifications</span>
              </button>
              <div className="h-10 w-10 rounded-full border-2 border-[#13a4ec]/20 p-0.5 overflow-hidden">
                <div className="w-full h-full object-cover rounded-full bg-slate-800 flex items-center justify-center">
                  <span className="material-symbols-outlined text-slate-400">account_circle</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-[1440px] mx-auto p-6">
        {/* Page Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
          <div>
            <h1 className="text-3xl md:text-4xl font-black tracking-tight bg-clip-text text-transparent bg-gradient-to-r from-white to-slate-500">
              Infrastructure Management
            </h1>
            <p className="text-slate-500 mt-2 text-sm font-medium">
              Monitor and manage your cloud infrastructure and services
            </p>
          </div>
          <div className="flex gap-3">
            <button 
              onClick={handleRefresh}
              className="bg-slate-700 hover:bg-slate-600 text-white px-4 py-2.5 rounded-lg font-bold text-sm flex items-center gap-2 transition-all"
            >
              <span className="material-symbols-outlined text-[18px]">refresh</span>
              Refresh
            </button>
            <button 
              onClick={() => setShowDeployModal(true)}
              className="bg-[#13a4ec] hover:bg-[#13a4ec]/90 text-white px-5 py-2.5 rounded-lg font-bold text-sm flex items-center gap-2 shadow-lg shadow-[#13a4ec]/30 transition-all active:scale-95"
            >
              <span className="material-symbols-outlined text-[18px]">add</span>
              Deploy Service
            </button>
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4 mb-6">
            <div className="flex items-center gap-3">
              <span className="material-symbols-outlined text-red-400">error</span>
              <p className="text-red-400">{error}</p>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex flex-wrap gap-2 mb-8 border-b border-[#233c48]">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-3 rounded-t-lg font-medium text-sm transition-colors ${
                activeTab === tab.id
                  ? 'bg-[#13a4ec]/10 text-[#13a4ec] border-b-2 border-[#13a4ec]'
                  : 'text-slate-400 hover:text-white hover:bg-[#233c48]/50'
              }`}
            >
              <span className="material-symbols-outlined text-[18px]">{tab.icon}</span>
              {tab.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="flex items-center gap-3">
              <div className="w-6 h-6 border-2 border-[#13a4ec]/30 border-t-[#13a4ec] rounded-full animate-spin"></div>
              <span className="text-slate-400">Loading infrastructure data...</span>
            </div>
          </div>
        ) : (
          <>
            {/* Overview Tab */}
            {activeTab === 'overview' && (
              <div className="space-y-8">
                {/* Metrics Cards */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                  <div className="bg-[#16252d] border border-[#233c48] rounded-xl p-6">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-[#13a4ec]/10 rounded-lg flex items-center justify-center">
                        <span className="material-symbols-outlined text-[#13a4ec] text-xl">trending_up</span>
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-white">{infrastructureData.metrics.totalRequests}</p>
                        <p className="text-slate-400 text-sm">Total Requests</p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-[#16252d] border border-[#233c48] rounded-xl p-6">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-green-500/10 rounded-lg flex items-center justify-center">
                        <span className="material-symbols-outlined text-green-400 text-xl">speed</span>
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-white">{infrastructureData.metrics.avgResponseTime}</p>
                        <p className="text-slate-400 text-sm">Avg Response Time</p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-[#16252d] border border-[#233c48] rounded-xl p-6">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-red-500/10 rounded-lg flex items-center justify-center">
                        <span className="material-symbols-outlined text-red-400 text-xl">error_outline</span>
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-white">{infrastructureData.metrics.errorRate}</p>
                        <p className="text-slate-400 text-sm">Error Rate</p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-[#16252d] border border-[#233c48] rounded-xl p-6">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-blue-500/10 rounded-lg flex items-center justify-center">
                        <span className="material-symbols-outlined text-blue-400 text-xl">schedule</span>
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-white">{infrastructureData.metrics.uptime}</p>
                        <p className="text-slate-400 text-sm">System Uptime</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Quick Status Grid */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Servers Status */}
                  <div className="bg-[#16252d] border border-[#233c48] rounded-xl p-6">
                    <h3 className="text-white font-bold text-lg mb-4 flex items-center gap-2">
                      <span className="material-symbols-outlined">dns</span>
                      Server Status
                    </h3>
                    <div className="space-y-3">
                      {infrastructureData.servers.slice(0, 3).map((server) => (
                        <div key={server.id} className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <span className={`w-2 h-2 rounded-full ${server.status === 'running' ? 'bg-green-400' : 'bg-yellow-400'}`}></span>
                            <span className="text-white font-medium">{server.name}</span>
                          </div>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(server.status)}`}>
                            {server.status}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Services Status */}
                  <div className="bg-[#16252d] border border-[#233c48] rounded-xl p-6">
                    <h3 className="text-white font-bold text-lg mb-4 flex items-center gap-2">
                      <span className="material-symbols-outlined">cloud</span>
                      Service Status
                    </h3>
                    <div className="space-y-3">
                      {infrastructureData.services.slice(0, 3).map((service) => (
                        <div key={service.id} className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <span className={`w-2 h-2 rounded-full ${service.status === 'running' ? 'bg-green-400' : 'bg-yellow-400'}`}></span>
                            <span className="text-white font-medium">{service.name}</span>
                          </div>
                          <span className="text-slate-400 text-sm">{service.uptime}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Servers Tab */}
            {activeTab === 'servers' && (
              <div className="bg-[#16252d] border border-[#233c48] rounded-xl overflow-hidden">
                <div className="p-6 border-b border-[#233c48]">
                  <h3 className="text-white font-bold text-lg">Server Management</h3>
                  <p className="text-slate-400 text-sm mt-1">Monitor server performance and resource usage</p>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-[#1a2b35]">
                      <tr>
                        <th className="text-left py-4 px-6 text-xs font-bold text-slate-500 uppercase tracking-wider">Server</th>
                        <th className="text-left py-4 px-6 text-xs font-bold text-slate-500 uppercase tracking-wider">Status</th>
                        <th className="text-left py-4 px-6 text-xs font-bold text-slate-500 uppercase tracking-wider">CPU</th>
                        <th className="text-left py-4 px-6 text-xs font-bold text-slate-500 uppercase tracking-wider">Memory</th>
                        <th className="text-left py-4 px-6 text-xs font-bold text-slate-500 uppercase tracking-wider">Disk</th>
                        <th className="text-left py-4 px-6 text-xs font-bold text-slate-500 uppercase tracking-wider">Location</th>
                        <th className="text-left py-4 px-6 text-xs font-bold text-slate-500 uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#233c48]">
                      {infrastructureData.servers.map((server) => (
                        <tr key={server.id} className="hover:bg-[#1a2b35] transition-colors">
                          <td className="py-4 px-6">
                            <div className="flex items-center gap-3">
                              <span className="material-symbols-outlined text-slate-400">dns</span>
                              <span className="text-white font-medium">{server.name}</span>
                            </div>
                          </td>
                          <td className="py-4 px-6">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(server.status)}`}>
                              {server.status}
                            </span>
                          </td>
                          <td className="py-4 px-6">
                            <div className="flex items-center gap-2">
                              <span className="text-white text-sm">{server.cpu}%</span>
                              <div className="w-16 bg-slate-700 h-2 rounded-full">
                                <div 
                                  className={`h-full rounded-full ${getUsageColor(server.cpu)}`}
                                  style={{width: `${server.cpu}%`}}
                                ></div>
                              </div>
                            </div>
                          </td>
                          <td className="py-4 px-6">
                            <div className="flex items-center gap-2">
                              <span className="text-white text-sm">{server.memory}%</span>
                              <div className="w-16 bg-slate-700 h-2 rounded-full">
                                <div 
                                  className={`h-full rounded-full ${getUsageColor(server.memory)}`}
                                  style={{width: `${server.memory}%`}}
                                ></div>
                              </div>
                            </div>
                          </td>
                          <td className="py-4 px-6">
                            <div className="flex items-center gap-2">
                              <span className="text-white text-sm">{server.disk}%</span>
                              <div className="w-16 bg-slate-700 h-2 rounded-full">
                                <div 
                                  className={`h-full rounded-full ${getUsageColor(server.disk)}`}
                                  style={{width: `${server.disk}%`}}
                                ></div>
                              </div>
                            </div>
                          </td>
                          <td className="py-4 px-6">
                            <span className="text-slate-400 text-sm">{server.location}</span>
                          </td>
                          <td className="py-4 px-6">
                            <div className="flex items-center gap-2">
                              <button className="text-slate-400 hover:text-[#13a4ec] p-1 rounded transition-colors">
                                <span className="material-symbols-outlined text-sm">settings</span>
                              </button>
                              <button className="text-slate-400 hover:text-[#13a4ec] p-1 rounded transition-colors">
                                <span className="material-symbols-outlined text-sm">restart_alt</span>
                              </button>
                              <button className="text-slate-400 hover:text-red-400 p-1 rounded transition-colors">
                                <span className="material-symbols-outlined text-sm">stop</span>
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Databases Tab */}
            {activeTab === 'databases' && (
              <div className="bg-[#16252d] border border-[#233c48] rounded-xl overflow-hidden">
                <div className="p-6 border-b border-[#233c48]">
                  <h3 className="text-white font-bold text-lg">Database Management</h3>
                  <p className="text-slate-400 text-sm mt-1">Monitor database health and performance</p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 p-6">
                  {infrastructureData.databases.map((db) => (
                    <div key={db.id} className="bg-[#0a1116] border border-[#233c48] rounded-lg p-6">
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-3">
                          <span className="material-symbols-outlined text-[#13a4ec]">storage</span>
                          <h4 className="text-white font-bold">{db.name}</h4>
                        </div>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(db.status)}`}>
                          {db.status}
                        </span>
                      </div>
                      <div className="space-y-3">
                        <div className="flex justify-between">
                          <span className="text-slate-400 text-sm">Type:</span>
                          <span className="text-white text-sm font-medium">{db.type}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-slate-400 text-sm">Connections:</span>
                          <span className="text-white text-sm font-medium">{db.connections}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-slate-400 text-sm">Size:</span>
                          <span className="text-white text-sm font-medium">{db.size}</span>
                        </div>
                      </div>
                      <div className="flex gap-2 mt-4">
                        <button className="flex-1 bg-[#233c48] hover:bg-[#13a4ec]/20 text-white px-3 py-2 rounded-lg text-sm font-medium transition-colors">
                          Monitor
                        </button>
                        <button className="px-3 py-2 border border-[#233c48] text-slate-400 hover:text-white hover:border-[#13a4ec]/50 rounded-lg text-sm font-medium transition-colors">
                          <span className="material-symbols-outlined text-sm">settings</span>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Services Tab */}
            {activeTab === 'services' && (
              <div className="bg-[#16252d] border border-[#233c48] rounded-xl overflow-hidden">
                <div className="p-6 border-b border-[#233c48]">
                  <h3 className="text-white font-bold text-lg">Service Management</h3>
                  <p className="text-slate-400 text-sm mt-1">Monitor microservices and applications</p>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-[#1a2b35]">
                      <tr>
                        <th className="text-left py-4 px-6 text-xs font-bold text-slate-500 uppercase tracking-wider">Service</th>
                        <th className="text-left py-4 px-6 text-xs font-bold text-slate-500 uppercase tracking-wider">Status</th>
                        <th className="text-left py-4 px-6 text-xs font-bold text-slate-500 uppercase tracking-wider">Version</th>
                        <th className="text-left py-4 px-6 text-xs font-bold text-slate-500 uppercase tracking-wider">Uptime</th>
                        <th className="text-left py-4 px-6 text-xs font-bold text-slate-500 uppercase tracking-wider">Requests/Day</th>
                        <th className="text-left py-4 px-6 text-xs font-bold text-slate-500 uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#233c48]">
                      {infrastructureData.services.map((service) => (
                        <tr key={service.id} className="hover:bg-[#1a2b35] transition-colors">
                          <td className="py-4 px-6">
                            <div className="flex items-center gap-3">
                              <span className="material-symbols-outlined text-slate-400">cloud</span>
                              <span className="text-white font-medium">{service.name}</span>
                            </div>
                          </td>
                          <td className="py-4 px-6">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(service.status)}`}>
                              {service.status}
                            </span>
                          </td>
                          <td className="py-4 px-6">
                            <span className="text-slate-400 text-sm">{service.version}</span>
                          </td>
                          <td className="py-4 px-6">
                            <span className="text-white text-sm">{service.uptime}</span>
                          </td>
                          <td className="py-4 px-6">
                            <span className="text-slate-400 text-sm">{service.requests}</span>
                          </td>
                          <td className="py-4 px-6">
                            <div className="flex items-center gap-2">
                              <button className="text-slate-400 hover:text-[#13a4ec] p-1 rounded transition-colors">
                                <span className="material-symbols-outlined text-sm">visibility</span>
                              </button>
                              <button className="text-slate-400 hover:text-[#13a4ec] p-1 rounded transition-colors">
                                <span className="material-symbols-outlined text-sm">restart_alt</span>
                              </button>
                              <button className="text-slate-400 hover:text-[#13a4ec] p-1 rounded transition-colors">
                                <span className="material-symbols-outlined text-sm">settings</span>
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Monitoring Tab */}
            {activeTab === 'monitoring' && (
              <div className="space-y-6">
                <div className="text-center py-12">
                  <span className="material-symbols-outlined text-slate-500 text-6xl mb-4 block">monitoring</span>
                  <h3 className="text-slate-300 font-bold text-lg mb-2">Monitoring Dashboard</h3>
                  <p className="text-slate-500 text-sm">
                    Real-time monitoring charts and metrics will be displayed here.
                  </p>
                </div>
              </div>
            )}

            {/* Logs Tab */}
            {activeTab === 'logs' && (
              <div className="space-y-6">
                <div className="text-center py-12">
                  <span className="material-symbols-outlined text-slate-500 text-6xl mb-4 block">description</span>
                  <h3 className="text-slate-300 font-bold text-lg mb-2">System Logs</h3>
                  <p className="text-slate-500 text-sm">
                    System logs and audit trails will be displayed here.
                  </p>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Deploy Service Modal */}
      {showDeployModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-[#16252d] border border-[#233c48] rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-[#233c48]">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold text-white">Deploy New Service</h3>
                <button
                  onClick={() => setShowDeployModal(false)}
                  className="text-slate-400 hover:text-white transition-colors"
                >
                  <span className="material-symbols-outlined">close</span>
                </button>
              </div>
              <p className="text-slate-400 text-sm mt-2">Configure and deploy a new service to your infrastructure</p>
            </div>

            <div className="p-6 space-y-6">
              {/* Service Name */}
              <div>
                <label className="block text-sm font-medium text-white mb-2">Service Name</label>
                <input
                  type="text"
                  placeholder="Enter service name (e.g., my-web-app)"
                  value={deployForm.serviceName}
                  onChange={(e) => setDeployForm(prev => ({ ...prev, serviceName: e.target.value }))}
                  className="w-full px-3 py-2 bg-[#0a1116] border border-[#233c48] rounded-lg text-white placeholder:text-slate-500 focus:ring-2 focus:ring-[#13a4ec] focus:border-transparent"
                />
              </div>

              {/* Service Type */}
              <div>
                <label className="block text-sm font-medium text-white mb-2">Service Type</label>
                <select 
                  value={deployForm.serviceType}
                  onChange={(e) => setDeployForm(prev => ({ ...prev, serviceType: e.target.value }))}
                  className="w-full px-3 py-2 bg-[#0a1116] border border-[#233c48] rounded-lg text-white focus:ring-2 focus:ring-[#13a4ec] focus:border-transparent"
                >
                  <option value="web-app">Web Application</option>
                  <option value="api">API Service</option>
                  <option value="database">Database</option>
                  <option value="worker">Background Worker</option>
                  <option value="microservice">Microservice</option>
                </select>
              </div>

              {/* Environment */}
              <div>
                <label className="block text-sm font-medium text-white mb-2">Environment</label>
                <select 
                  value={deployForm.environment}
                  onChange={(e) => setDeployForm(prev => ({ ...prev, environment: e.target.value }))}
                  className="w-full px-3 py-2 bg-[#0a1116] border border-[#233c48] rounded-lg text-white focus:ring-2 focus:ring-[#13a4ec] focus:border-transparent"
                >
                  <option value="development">Development</option>
                  <option value="staging">Staging</option>
                  <option value="production">Production</option>
                </select>
              </div>

              {/* Replicas */}
              <div>
                <label className="block text-sm font-medium text-white mb-2">Replicas</label>
                <input
                  type="number"
                  min="1"
                  max="10"
                  value={deployForm.replicas}
                  onChange={(e) => setDeployForm(prev => ({ ...prev, replicas: parseInt(e.target.value) }))}
                  className="w-full px-3 py-2 bg-[#0a1116] border border-[#233c48] rounded-lg text-white focus:ring-2 focus:ring-[#13a4ec] focus:border-transparent"
                />
                <p className="text-slate-500 text-xs mt-1">Number of instances to run (1-10)</p>
              </div>

              {/* Resources */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-white mb-2">CPU Limit</label>
                  <select 
                    value={deployForm.resources.cpu}
                    onChange={(e) => setDeployForm(prev => ({ 
                      ...prev, 
                      resources: { ...prev.resources, cpu: e.target.value }
                    }))}
                    className="w-full px-3 py-2 bg-[#0a1116] border border-[#233c48] rounded-lg text-white focus:ring-2 focus:ring-[#13a4ec] focus:border-transparent"
                  >
                    <option value="100m">100m (0.1 CPU)</option>
                    <option value="250m">250m (0.25 CPU)</option>
                    <option value="500m">500m (0.5 CPU)</option>
                    <option value="1000m">1000m (1 CPU)</option>
                    <option value="2000m">2000m (2 CPU)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-white mb-2">Memory Limit</label>
                  <select 
                    value={deployForm.resources.memory}
                    onChange={(e) => setDeployForm(prev => ({ 
                      ...prev, 
                      resources: { ...prev.resources, memory: e.target.value }
                    }))}
                    className="w-full px-3 py-2 bg-[#0a1116] border border-[#233c48] rounded-lg text-white focus:ring-2 focus:ring-[#13a4ec] focus:border-transparent"
                  >
                    <option value="128Mi">128Mi</option>
                    <option value="256Mi">256Mi</option>
                    <option value="512Mi">512Mi</option>
                    <option value="1Gi">1Gi</option>
                    <option value="2Gi">2Gi</option>
                    <option value="4Gi">4Gi</option>
                  </select>
                </div>
              </div>

              {/* Port Configuration */}
              <div>
                <label className="block text-sm font-medium text-white mb-2">Port Configuration</label>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs text-slate-400 mb-1">External Port</label>
                    <input
                      type="number"
                      min="1"
                      max="65535"
                      value={deployForm.ports[0].port}
                      onChange={(e) => setDeployForm(prev => ({ 
                        ...prev, 
                        ports: [{ ...prev.ports[0], port: parseInt(e.target.value) }]
                      }))}
                      className="w-full px-3 py-2 bg-[#0a1116] border border-[#233c48] rounded-lg text-white focus:ring-2 focus:ring-[#13a4ec] focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-slate-400 mb-1">Target Port</label>
                    <input
                      type="number"
                      min="1"
                      max="65535"
                      value={deployForm.ports[0].targetPort}
                      onChange={(e) => setDeployForm(prev => ({ 
                        ...prev, 
                        ports: [{ ...prev.ports[0], targetPort: parseInt(e.target.value) }]
                      }))}
                      className="w-full px-3 py-2 bg-[#0a1116] border border-[#233c48] rounded-lg text-white focus:ring-2 focus:ring-[#13a4ec] focus:border-transparent"
                    />
                  </div>
                </div>
              </div>

              {/* Deployment Summary */}
              <div className="bg-[#0a1116] border border-[#233c48] rounded-lg p-4">
                <h4 className="text-white font-medium mb-3 flex items-center gap-2">
                  <span className="material-symbols-outlined text-[#13a4ec]">info</span>
                  Deployment Summary
                </h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-400">Service:</span>
                    <span className="text-white">{deployForm.serviceName || 'Not specified'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">Type:</span>
                    <span className="text-white">{deployForm.serviceType}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">Environment:</span>
                    <span className="text-white">{deployForm.environment}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">Resources:</span>
                    <span className="text-white">{deployForm.resources.cpu} CPU, {deployForm.resources.memory} RAM</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">Replicas:</span>
                    <span className="text-white">{deployForm.replicas}</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="p-6 border-t border-[#233c48] flex gap-3">
              <button
                onClick={() => setShowDeployModal(false)}
                className="flex-1 px-4 py-2 border border-[#233c48] text-slate-400 hover:text-white hover:border-[#13a4ec]/50 rounded-lg font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDeployService}
                disabled={!deployForm.serviceName.trim() || loading}
                className="flex-1 bg-[#13a4ec] hover:bg-[#13a4ec]/90 disabled:bg-slate-600 disabled:cursor-not-allowed text-white px-4 py-2 rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                    Deploying...
                  </>
                ) : (
                  <>
                    <span className="material-symbols-outlined text-sm">rocket_launch</span>
                    Deploy Service
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InfrastructurePage;